
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { logger } from '@/utils/logger';
import { createOpenAILogError } from '@/utils/loggerTypes';
import { makeOpenAIRequest } from './apiClient';
import { getSystemPrompt, getUserPrompt } from './prompts';
import { parseOpenAIResponse, createErrorResult } from './responseParser';
import { API_KEYS } from '@/utils/apiKeys';

/**
 * Generate a deep content analysis for the provided HTML content
 */
export async function analyzeContent(
  content: string,
  url?: string
): Promise<DeepAnalysisResult> {
  try {
    // Use hardcoded API key and model
    const apiKey = API_KEYS.openai.deepAnalysis;
    // Use the most appropriate model from the OpenAI offerings
    const model = 'gpt-4o';
    
    console.log(`Preparing content analysis with model: ${model}`);
    console.log(`API key present: ${apiKey ? 'Yes' : 'No'}, length: ${apiKey?.length || 0}`);
    
    // Validate API key format
    if (!apiKey || !apiKey.startsWith('sk-')) {
      throw new Error('Invalid API key format. Please check your API key.');
    }
    
    // Generate a content hash to help with debugging and caching
    const contentPreview = content.substring(0, 100).replace(/\s+/g, ' ').trim();
    const contentLength = content.length;
    console.log(`Analyzing content: "${contentPreview}..."  (${contentLength} chars)`);

  // Get cached analysis if available to improve consistency
  const cachedResult = checkAnalysisCache(url || contentPreview);
  if (cachedResult) {
    console.log('Using cached analysis result');
    console.log('Raw cached result before processing:', JSON.stringify(cachedResult, null, 2));
    
    return cachedResult;
  }
  
  // Clear cache periodically to ensure fresh results
  if (Math.random() < 0.1) { // 10% chance to clear cache
    clearAnalysisCache();
  }
    
    const systemPrompt = getSystemPrompt();
    const userPrompt = getUserPrompt(content);
    
    console.log('Sending content for analysis to OpenAI');
    
    // Use function calling - no retry needed as schema is enforced
    let result = await attemptAnalysis(apiKey, model, systemPrompt, userPrompt);
    
    console.log('Raw OpenAI result before processing:', JSON.stringify(result, null, 2));
    
    console.log('🚨 contentAnalysisService - Analysis result category_scores:', Object.keys(result.category_scores || {}));
    
    // Store result in cache if URL is provided
    if (url) {
      storeAnalysisCache(url, result);
    }
    
    console.log('[DEBUG] contentAnalysisService - Final result category_scores:', Object.keys(result.category_scores || {}));
    
    return result;
  } catch (error) {
    const errorMessage = (error as Error).message;
    console.error('Error in content analysis:', errorMessage);
    
    logger.error('Content analysis failed', 
      createOpenAILogError(errorMessage, {
        operation: 'chat',
        model: 'gpt-4o',
        component: 'DeepContentAnalysisService'
      })
    );
    
    // Return a fallback result
    return createErrorResult(errorMessage);
  }
}

// REMOVED: This function is no longer used in the clean rebuild

// Simple in-memory cache for analysis results
const analysisCache: Record<string, {result: DeepAnalysisResult, timestamp: number}> = {};
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Check if there's a cached analysis result
 */
function checkAnalysisCache(key: string): DeepAnalysisResult | null {
  const cached = analysisCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRATION) {
    return cached.result;
  }
  return null;
}

/**
 * Store analysis result in cache
 */
function storeAnalysisCache(key: string, result: DeepAnalysisResult): void {
  analysisCache[key] = {
    result,
    timestamp: Date.now()
  };
}

/**
 * Attempt analysis with OpenAI
 */
async function attemptAnalysis(apiKey: string, model: string, systemPrompt: string, userPrompt: string): Promise<DeepAnalysisResult> {
  const responseText = await makeOpenAIRequest(
    apiKey,
    model,
    systemPrompt,
    userPrompt,
    0.4, // Higher temperature for more varied and less repetitive results
    2000,
    true // Use function calling schema
  );
  
  console.log('Received analysis response, parsing...');
  return parseOpenAIResponse(responseText);
}

/**
 * Calculate AI readiness score based on content structure as fallback
 */
function calculateAIReadinessFallback(content: string): number {
  let score = 0;
  
  // Check for headings structure (0-5 points)
  const headings = (content.match(/<h[1-6][^>]*>/gi) || []).length;
  if (headings > 0) score += Math.min(headings, 5);
  
  // Check for lists (0-3 points)
  const lists = (content.match(/<[uo]l[^>]*>/gi) || []).length;
  if (lists > 0) score += Math.min(lists, 3);
  
  // Check for semantic elements (0-4 points)
  const semanticElements = (content.match(/<(article|section|nav|aside|header|footer|main)[^>]*>/gi) || []).length;
  if (semanticElements > 0) score += Math.min(semanticElements, 4);
  
  // Check for structured data indicators (0-3 points)
  const structuredData = content.includes('schema.org') || content.includes('application/ld+json');
  if (structuredData) score += 3;
  
  // Check for meta descriptions and titles (0-2 points)
  const metaTags = (content.match(/<meta[^>]*>/gi) || []).length;
  if (metaTags > 3) score += 2;
  
  // Check for alt attributes on images (0-3 points)
  const altTags = (content.match(/alt\s*=\s*["'][^"']+["']/gi) || []).length;
  if (altTags > 0) score += Math.min(altTags, 3);
  
  console.log(`Calculated AI Readiness fallback score: ${score}/20`);
  return Math.min(score, 20);
}

/**
 * Clear the analysis cache to fix the score 52 issue
 */
export function clearAnalysisCache(): void {
  Object.keys(analysisCache).forEach(key => delete analysisCache[key]);
  console.log('Analysis cache cleared to ensure fresh results');
}
